import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { InModuleContext } from '../components/Bits';
import {
  clampSpan,
  clearLayout,
  defaultLayout,
  loadLayout,
  mergeLayout,
  moveModule,
  saveLayout,
  type DashboardLayout,
} from './layout';

export interface DashboardModule {
  id: string;
  title: string;
  /** Inhalt des Moduls. Gibt er `null` zurück, wird das Modul gerade nicht gebraucht. */
  content: ReactNode;
  /** Manche Module brauchen keine Breitenwahl, etwa schmale Listen. */
  resizable?: boolean;
}

/** Höhe einer Rasterzeile in Pixeln. Je feiner das Raster, desto dichter schließen die Module an. */
const ROW = 8;
const GAP = 16;

/**
 * Das Raster des Spielleiter-Dashboards.
 *
 * Die Module liegen in drei Spalten. Verschoben wird per Ziehen am Titel, ergänzend gibt es
 * Pfeilknöpfe für Touch und Tastatur. Damit unter einem kurzen Modul keine Lücke entsteht, nur
 * weil daneben eine lange Liste steht, misst jedes Modul seine Höhe und belegt entsprechend viele
 * feine Rasterzeilen. Dadurch rücken die Module senkrecht dicht zusammen.
 *
 * Position, Breite und Zustand sind reine Anzeigedaten: Sie berühren den Spielzustand nicht, und
 * ein Umbau während der Partie lädt nichts neu.
 */
export function DashboardGrid({ modules }: { modules: DashboardModule[] }) {
  const ids = modules.map((m) => m.id);
  const [layout, setLayout] = useState<DashboardLayout>(() => mergeLayout(loadLayout(), ids));
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  // Neue Module (etwa nach einem Update) ergänzen das bestehende Layout, statt es zu verwerfen.
  useEffect(() => {
    setLayout((current) => {
      const fehlt = ids.filter((id) => !current.order.includes(id));
      const weg = current.order.filter((id) => !ids.includes(id));
      return fehlt.length === 0 && weg.length === 0 ? current : mergeLayout(current, ids);
    });
  }, [ids.join(',')]);

  function update(next: DashboardLayout) {
    setLayout(next);
    saveLayout(next);
  }

  const byId = new Map(modules.map((m) => [m.id, m]));
  const visible = layout.order
    .map((id) => byId.get(id))
    .filter((m): m is DashboardModule => !!m && m.content !== null);

  return (
    <div className="dashboard">
      <div className="dashboard-bar">
        <button
          type="button"
          className={layout.locked ? '' : 'primary'}
          onClick={() => update({ ...layout, locked: !layout.locked })}
        >
          {layout.locked ? 'Layout bearbeiten' : 'Layout sperren'}
        </button>
        {!layout.locked && (
          <>
            <span className="tiny muted">
              Module am Titel ziehen oder mit den Pfeilen verschieben. Mit − und + die Breite ändern.
            </span>
            <button
              type="button"
              className="ghost tiny"
              onClick={() => {
                clearLayout();
                update({ ...defaultLayout(ids), locked: false });
              }}
            >
              Layout zurücksetzen
            </button>
          </>
        )}
      </div>

      <div className="dashboard-grid" style={{ gridAutoRows: `${ROW}px`, gap: `${GAP}px` }}>
        {visible.map((module) => (
          <ModuleCard
            key={module.id}
            module={module}
            index={layout.order.indexOf(module.id)}
            layout={layout}
            update={update}
            dragging={dragging}
            setDragging={setDragging}
            over={over}
            setOver={setOver}
          />
        ))}
      </div>
    </div>
  );
}

function ModuleCard({
  module,
  index,
  layout,
  update,
  dragging,
  setDragging,
  over,
  setOver,
}: {
  module: DashboardModule;
  index: number;
  layout: DashboardLayout;
  update: (next: DashboardLayout) => void;
  dragging: string | null;
  setDragging: (id: string | null) => void;
  over: string | null;
  setOver: (id: string | null) => void;
}) {
  const inner = useRef<HTMLDivElement | null>(null);
  const [rows, setRows] = useState(1);
  const collapsed = !!layout.collapsed[module.id];
  const span = clampSpan(layout.span[module.id] ?? 1);
  const resizable = module.resizable !== false;

  // Höhe messen und in Rasterzeilen umrechnen – neu, sobald sich der Inhalt ändert.
  useLayoutEffect(() => {
    const element = inner.current;
    if (!element) return;
    const messen = () => {
      const höhe = element.getBoundingClientRect().height;
      setRows(Math.max(1, Math.ceil((höhe + GAP) / (ROW + GAP))));
    };
    messen();
    if (typeof ResizeObserver === 'undefined') return;
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(element);
    return () => beobachter.disconnect();
  }, [collapsed, span, module.content]);

  return (
    <section
      className={`module${dragging === module.id ? ' dragging' : ''}${over === module.id ? ' drop-target' : ''}${
        collapsed ? ' collapsed' : ''
      }`}
      style={{ gridColumn: `span ${span}`, gridRow: `span ${rows}` }}
      draggable={!layout.locked}
      onDragStart={(e) => {
        setDragging(module.id);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', module.id);
      }}
      onDragEnd={() => {
        setDragging(null);
        setOver(null);
      }}
      onDragOver={(e) => {
        if (layout.locked || !dragging || dragging === module.id) return;
        e.preventDefault();
        setOver(module.id);
      }}
      onDragLeave={() => setOver(null)}
      onDrop={(e) => {
        e.preventDefault();
        const bewegt = e.dataTransfer.getData('text/plain') || dragging;
        setOver(null);
        setDragging(null);
        if (!bewegt || bewegt === module.id) return;
        update({ ...layout, order: moveModule(layout.order, bewegt, layout.order.indexOf(module.id)) });
      }}
    >
      <div className="panel module-inner" ref={inner}>
        <header className="module-head">
          <button
            type="button"
            className="ghost module-title"
            aria-expanded={!collapsed}
            onClick={() => update({ ...layout, collapsed: { ...layout.collapsed, [module.id]: !collapsed } })}
          >
            <span aria-hidden="true">{collapsed ? '▸' : '▾'}</span> {module.title}
          </button>

          {!layout.locked && (
            <span className="module-tools">
              <button
                type="button"
                className="tiny ghost"
                aria-label="Nach vorn"
                disabled={index === 0}
                onClick={() => update({ ...layout, order: moveModule(layout.order, module.id, index - 1) })}
              >
                ◀
              </button>
              <button
                type="button"
                className="tiny ghost"
                aria-label="Nach hinten"
                disabled={index === layout.order.length - 1}
                onClick={() => update({ ...layout, order: moveModule(layout.order, module.id, index + 1) })}
              >
                ▶
              </button>
              {resizable && (
                <>
                  <button
                    type="button"
                    className="tiny ghost"
                    aria-label="Schmaler"
                    disabled={span <= 1}
                    onClick={() => update({ ...layout, span: { ...layout.span, [module.id]: clampSpan(span - 1) } })}
                  >
                    −
                  </button>
                  <button
                    type="button"
                    className="tiny ghost"
                    aria-label="Breiter"
                    disabled={span >= 3}
                    onClick={() => update({ ...layout, span: { ...layout.span, [module.id]: clampSpan(span + 1) } })}
                  >
                    +
                  </button>
                </>
              )}
            </span>
          )}
        </header>

        {/*
          Eingeklappt wird nur die Anzeige: Der Inhalt bleibt eingehängt, damit laufende Vorgänge
          im Modul weiterlaufen und nichts neu geladen werden muss.
        */}
        <div className="module-body" hidden={collapsed}>
          <InModuleContext.Provider value={true}>{module.content}</InModuleContext.Provider>
        </div>
      </div>
    </section>
  );
}
